import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { Store } from '@ngxs/store';
import { ValidationsHelper } from '@nibtravel/shared/ui/forms/validation';
import {
  IFormFieldValidation,
  IPolicyHolderAgeConfig,
  IPolicyHoldersInput,
  ValidationType,
} from '@nibtravel/shared/util/models';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { TravellerAgeSelector } from './age-input.selector';
import { PolicyHolderService } from '@nibtravel/shared/util/service';

@Injectable({ providedIn: 'root' })
export class AgeInputService {
  constructor(
    private store: Store,
    private policyHolderService: PolicyHolderService
  ) {}

  getAgeInputValidations(): Observable<IFormFieldValidation[]> {
    return this.store
      .select(TravellerAgeSelector.policyHolderConfig)
      .pipe(map((config) => this.createValidations(config)));
  }

  arrayValidation(): IFormFieldValidation[] {
    return [
      {
        type: ValidationType.Custom,
        custom: {
          name: 'atLeastOne',
          validator: () => this.atLeastOneAge(),
        },
      },
      {
        type: ValidationType.Custom,
        custom: {
          name: 'maxAdults',
          validator: () => this.maxAdults(),
        },
      },
    ];
  }

  private createValidations(
    config: IPolicyHolderAgeConfig | undefined
  ): IFormFieldValidation[] {
    const create = (config: IPolicyHolderAgeConfig) => {
      const minAge = config.minTravellerAge;
      const maxAge = config.maxTravellerAge;

      return [
        {
          ...ValidationsHelper.Max,
          parameter: maxAge,
          message: (_: AbstractControl, age: any) =>
            `The max age is ${age.max} and you entered ${age.actual}`,
        },
        {
          ...ValidationsHelper.Min,
          parameter: minAge,
          message: (_: AbstractControl, age: any) =>
            `The min age is ${age.min} and you entered ${age.actual}`,
        },
      ];
    };

    return config ? create(config) : [];
  }

  private atLeastOneAge(): ValidatorFn {
    return (formArray: AbstractControl): ValidationErrors | null => {
      const arrayControls = (formArray as FormArray).controls;
      const hasAtLeastOneAge = arrayControls?.some((phGroup) => {
        const ageControl = phGroup?.get('age');
        const hasValue = ageControl?.value !== null && ageControl?.value !== '';
        return hasValue;
      });

      return hasAtLeastOneAge || formArray.pristine
        ? null
        : { atLeastOne: 'Please add age of at least one traveller' };
    };
  }

  private maxAdults(): ValidatorFn {
    const config = this.store.selectSnapshot(
      TravellerAgeSelector.policyHolderConfig
    ) as IPolicyHolderAgeConfig;

    return (formArray: AbstractControl): ValidationErrors | null => {
      const policyHolders = formArray.value as IPolicyHoldersInput[];
      policyHolders.forEach((item) => {
        if (item.age != null && typeof item.age === 'string') {
          item.age = Number.parseInt(item.age);
        }
      });

      const isAmbiguity =
        this.policyHolderService.checkAmbiguity(policyHolders);
      const error = this.policyHolderService.countError(policyHolders, config);

      return !isAmbiguity && error ? { maxAdults: error } : null;
    };
  }
}
